/**
 * Layout and catalog helpers for the home dashboard.
 */

import * as LucideIcons from 'lucide-react';
import type { AppMode } from '@/contexts/ModeContext';
import type {
  DashboardSection,
  DashboardSectionType,
  SavedDashboardLayout,
  SavedDashboardSectionPref,
} from '@/types/dashboard.types';
import type { ElementWidth, InterfaceElement } from '@/types/interface-elements.types';
import { getRoleLabelPlural } from '@/utils/role-label-utils';
import {
  ALWAYS_VISIBLE_DASHBOARD_SECTION_TYPES,
  DASHBOARD_COMPLEMENTARY_WIDTH,
  DASHBOARD_GRID_COLUMNS,
  DASHBOARD_MAX_SECTIONS_PER_ROW,
  DASHBOARD_PREFERENCE_KEY,
  DASHBOARD_REMOTE_PREFERENCE_KEY,
  DASHBOARD_SECTION_CATALOG,
  DROP_ZONE_AFTER_ROW_PREFIX,
  ROW_DROPPABLE_PREFIX,
} from './dashboard-configs';

/** One wrap row of dashboard sections (sum of widths). */
export interface VisualRow {
  sections: DashboardSection[];
  totalCols: number;
}

/** Where a dragged section would insert relative to a wrap-row neighbor. */
type InsertIndicator = {
  insertIdx: number;
  sectionId: string;
  position: 'left' | 'right';
};

/** Insert target inside a wrap row. */
type DashboardDropPosition = {
  targetRowIdx: number;
  insertIdx: number;
};

/** Grid child: drop gap, source-row placeholder, or a wrap row. */
type VisualItem =
  | { kind: 'drop-zone'; afterRowIdx: number }
  | { kind: 'source-placeholder' }
  | { kind: 'row'; row: VisualRow; rowIdx: number };

/** One slot inside a wrap row: a section or the inline insert placeholder. */
type RowItem =
  | { kind: 'section'; section: DashboardSection }
  | { kind: 'drop-placeholder'; width: ElementWidth };

/** Preference key for the current product mode. */
export function getDashboardPreferenceKey(mode: AppMode): string {
  return mode === 'remote' ? DASHBOARD_REMOTE_PREFERENCE_KEY : DASHBOARD_PREFERENCE_KEY;
}

/** Droppable id for the gap after wrap row `afterRowIdx` (`-1` is the top gap). */
export function dropZoneAfterRowId(afterRowIdx: number): string {
  return `${DROP_ZONE_AFTER_ROW_PREFIX}${afterRowIdx}`;
}

/** Droppable id covering wrap row `rowIdx`. */
export function rowDroppableId(rowIdx: number): string {
  return `${ROW_DROPPABLE_PREFIX}${rowIdx}`;
}

/** True when `id` is a between-row drop zone. */
export function isDropZoneAfterRowId(id: string): boolean {
  return id.startsWith(DROP_ZONE_AFTER_ROW_PREFIX);
}

/** True when `id` is an in-row droppable. */
export function isRowDroppableId(id: string): boolean {
  return id.startsWith(ROW_DROPPABLE_PREFIX);
}

/** Wrap-row index encoded in a between-row drop-zone id. */
function parseDropZoneAfterRowIdx(id: string): number {
  return Number.parseInt(id.replace(DROP_ZONE_AFTER_ROW_PREFIX, ''), 10);
}

/** Wrap-row index encoded in an in-row droppable id. */
export function parseRowDroppableIdx(id: string): number {
  return Number.parseInt(id.replace(ROW_DROPPABLE_PREFIX, ''), 10);
}

/** Resolve a Lucide export by name; fall back to {@link HelpCircle}. */
export function getLucideIcon(iconName: string): LucideIcons.LucideIcon {
  const icons = LucideIcons as unknown as Record<string, LucideIcons.LucideIcon>;
  return icons[iconName] || LucideIcons.HelpCircle;
}

/** Build the default section list (role titles from the catalog). */
function buildDefaultDashboardSections(): DashboardSection[] {
  return DASHBOARD_SECTION_CATALOG.map((item) => ({
    id: item.id,
    type: item.type,
    order: item.order,
    width: 'full',
    visible: true,
    title: item.roleCode ? getRoleLabelPlural(item.roleCode) : (item.title ?? ''),
  }));
}

/** Sidebar menu `sectionType` (or stripped `menu-*` id) the user can open. */
function collectAccessibleMenuSectionTypes(sidebarMenus: InterfaceElement[]): Set<string> {
  return new Set(
    sidebarMenus
      .filter((el) => el.elementType === 'menu' && el.elementId.startsWith('menu-'))
      .map((el) => {
        const config = el.config as { sectionType?: string } | undefined;
        return config?.sectionType ?? el.elementId.replace('menu-', '');
      }),
  );
}

/** True when the widget has no sidebar menu and stays on the dashboard. */
function isAlwaysVisibleDashboardSection(type: DashboardSectionType): boolean {
  return ALWAYS_VISIBLE_DASHBOARD_SECTION_TYPES.includes(type);
}

/** Keep widgets the user can open, plus always-visible ones. */
function filterSectionsByAccess(
  sections: DashboardSection[],
  accessibleMenus: Set<string>,
): DashboardSection[] {
  return sections.filter((section) => {
    if (isAlwaysVisibleDashboardSection(section.type)) return true;
    return (
      accessibleMenus.has(section.type) || accessibleMenus.has(section.id.replace(/^section-/, ''))
    );
  });
}

/** Merge saved order / width / visibility onto the accessible catalog. */
function applySavedDashboardLayout(
  sections: DashboardSection[],
  savedSections: SavedDashboardSectionPref[],
): DashboardSection[] {
  return sections
    .map((section) => {
      const saved = savedSections.find((s) => s.id === section.id);
      return saved
        ? { ...section, order: saved.order, width: saved.width, visible: saved.visible ?? true }
        : section;
    })
    .sort((a, b) => a.order - b.order);
}

/** Catalog filtered by sidebar RBAC, then overlaid with saved prefs. */
export function initializeDashboardSections(
  sidebarMenus: InterfaceElement[],
  savedLayout: SavedDashboardLayout | undefined,
): DashboardSection[] {
  const allSections = buildDefaultDashboardSections();
  const accessibleMenus = collectAccessibleMenuSectionTypes(sidebarMenus);
  const filtered = filterSectionsByAccess(allSections, accessibleMenus);
  if (!savedLayout?.sections) return filtered;
  return applySavedDashboardLayout(filtered, savedLayout.sections);
}

/** Flip `visible` on the matching section. */
export function toggleSectionVisibility(
  sections: DashboardSection[],
  sectionId: string,
): DashboardSection[] {
  return sections.map((s) => (s.id === sectionId ? { ...s, visible: !(s.visible ?? true) } : s));
}

/** Editor list: visible widgets first, then hidden (order otherwise unchanged). */
export function sortSectionsForEditor(sections: DashboardSection[]): DashboardSection[] {
  return [...sections].sort((a, b) => {
    const aVisible = a.visible ?? true;
    const bVisible = b.visible ?? true;
    if (aVisible === bVisible) return 0;
    return aVisible ? -1 : 1;
  });
}

/** Persist payload: order / width / visibility only. */
export function toSavedDashboardLayout(sections: DashboardSection[]): Record<string, unknown> {
  return {
    sections: sections.map((s) => ({
      id: s.id,
      order: s.order,
      width: s.width,
      visible: s.visible ?? true,
    })),
  };
}

/** Widgets the live dashboard should render. */
export function visibleDashboardSections(sections: DashboardSection[]): DashboardSection[] {
  return sections.filter((s) => s.visible !== false);
}

/** Map a section width token to a 12-column span. */
function getColSpan(width: ElementWidth): number {
  switch (width) {
    case 'full':
      return DASHBOARD_GRID_COLUMNS;
    case '2/3':
      return 8;
    case '1/2':
      return 6;
    case '1/3':
      return 4;
    default:
      return DASHBOARD_GRID_COLUMNS;
  }
}

/** Equal-share width for every section in a wrap row (max 3). */
function widthForSectionCount(count: number): ElementWidth {
  if (count >= DASHBOARD_MAX_SECTIONS_PER_ROW) return '1/3';
  if (count === 2) return '1/2';
  return 'full';
}

/** Insert index from cursor X: 1-section row is left/right; 2-section row is thirds. */
export function computeRowInsertIdx(
  sectionCount: number,
  relX: number,
  layoutWidth: number,
): number {
  if (sectionCount === 2) {
    if (relX < layoutWidth / 3) return 0;
    if (relX < layoutWidth / 2) return 1;
    return 2;
  }
  return relX < (layoutWidth - 16) / 2 ? 0 : 1;
}

/** Neighbor + side used to draw the inline insert indicator. */
export function computeRowInsertIndicator(
  sectionCount: number,
  relX: number,
  layoutWidth: number,
  rowSections: DashboardSection[],
): InsertIndicator | null {
  if (sectionCount !== 1 && sectionCount !== 2) return null;

  const insertIdx = computeRowInsertIdx(sectionCount, relX, layoutWidth);
  if (sectionCount === 1) {
    return {
      insertIdx,
      sectionId: rowSections[0]?.id ?? '',
      position: insertIdx === 0 ? 'left' : 'right',
    };
  }
  if (insertIdx === 0) {
    return { insertIdx, sectionId: rowSections[0]?.id ?? '', position: 'left' };
  }
  if (insertIdx === 1) {
    return { insertIdx, sectionId: rowSections[0]?.id ?? '', position: 'right' };
  }
  return { insertIdx, sectionId: rowSections[1]?.id ?? '', position: 'right' };
}

/** Write equal-share widths for every section already in `rows`. */
export function applyEqualRowWidths(rows: VisualRow[], target: Record<string, ElementWidth>): void {
  rows.forEach((row) => {
    const w = widthForSectionCount(row.sections.length);
    row.sections.forEach((s) => {
      target[s.id] = w;
    });
  });
}

/** Pack sections into wrap rows that do not exceed 12 columns. */
export function calculateRows(sectionList: DashboardSection[]): VisualRow[] {
  const rows: VisualRow[] = [];
  let currentRow: VisualRow = { sections: [], totalCols: 0 };

  for (const section of sectionList) {
    const cols = getColSpan(section.width);
    if (currentRow.totalCols + cols > DASHBOARD_GRID_COLUMNS && currentRow.sections.length > 0) {
      rows.push(currentRow);
      currentRow = { sections: [section], totalCols: cols };
    } else {
      currentRow.sections.push(section);
      currentRow.totalCols += cols;
    }
  }

  if (currentRow.sections.length > 0) rows.push(currentRow);
  return rows;
}

/** Index of the wrap row that currently contains `sectionId`. */
function findRowIndex(sectionId: string, rows: VisualRow[]): number {
  return rows.findIndex((row) => row.sections.some((s) => s.id === sectionId));
}

/** Preview widths as if `activeId` were already inserted into `targetRowIdx`. */
export function calculateAllRowsPreview(
  rows: VisualRow[],
  targetRowIdx: number,
  activeId: string,
): Record<string, ElementWidth> {
  const newWidths: Record<string, ElementWidth> = {};

  rows.forEach((row, idx) => {
    if (idx === targetRowIdx) {
      const w = widthForSectionCount(row.sections.length + 1);
      row.sections.forEach((s) => {
        newWidths[s.id] = w;
      });
      newWidths[activeId] = w;
    } else {
      const w = widthForSectionCount(row.sections.length);
      row.sections.forEach((s) => {
        newWidths[s.id] = w;
      });
    }
  });

  return newWidths;
}

/** Flatten intended wrap rows and reassign equal widths + sequential `order`. */
function buildSectionsFromRows(intendedRows: DashboardSection[][]): DashboardSection[] {
  const result: DashboardSection[] = [];

  intendedRows.forEach((row) => {
    const w = widthForSectionCount(row.length);
    row.forEach((s) => result.push({ ...s, width: w }));
  });

  result.forEach((s, idx) => {
    s.order = idx;
  });

  return result;
}

/** When the dragged section was alone, keep a source-row hole and skip its drop gap. */
function appendRowsWithSourcePlaceholder(
  visualItems: VisualItem[],
  rows: VisualRow[],
  rowsWithActive: VisualRow[],
  srcRowIdxInAll: number,
): void {
  let withoutIdx = 0;
  for (let allIdx = 0; allIdx < rowsWithActive.length; allIdx++) {
    if (allIdx === srcRowIdxInAll) {
      visualItems.push({ kind: 'source-placeholder' });
      continue;
    }
    const row = rows[withoutIdx];
    if (!row) continue;
    if (allIdx + 1 === srcRowIdxInAll) {
      visualItems.push({ kind: 'row', row, rowIdx: withoutIdx });
    } else {
      visualItems.push(
        { kind: 'row', row, rowIdx: withoutIdx },
        { kind: 'drop-zone', afterRowIdx: withoutIdx },
      );
    }
    withoutIdx++;
  }
}

/** Interleave wrap rows with between-row drop zones (and the source placeholder). */
function buildVisualItems(
  rows: VisualRow[],
  rowsWithActive: VisualRow[] | null,
  activeWasAlone: boolean,
  srcRowIdxInAll: number,
): VisualItem[] {
  const visualItems: VisualItem[] = [];
  const suppressDzTop = activeWasAlone && srcRowIdxInAll === 0;
  if (!suppressDzTop) {
    visualItems.push({ kind: 'drop-zone', afterRowIdx: -1 });
  }

  if (activeWasAlone && rowsWithActive) {
    appendRowsWithSourcePlaceholder(visualItems, rows, rowsWithActive, srcRowIdxInAll);
    return visualItems;
  }

  rows.forEach((row, idx) => {
    visualItems.push({ kind: 'row', row, rowIdx: idx }, { kind: 'drop-zone', afterRowIdx: idx });
  });
  return visualItems;
}

/** Rows without the active section, plus a source hole if it was the only item in its row. */
export function resolveVisualItems(
  sections: DashboardSection[],
  activeId: string | null,
): VisualItem[] {
  const sectionsWithoutActive = activeId ? sections.filter((s) => s.id !== activeId) : sections;
  const rows = calculateRows(sectionsWithoutActive);
  const rowsWithActive = activeId ? calculateRows(sections) : null;
  const srcRowIdxInAll = activeId && rowsWithActive ? findRowIndex(activeId, rowsWithActive) : -1;
  const activeWasAlone =
    srcRowIdxInAll >= 0 && (rowsWithActive?.[srcRowIdxInAll]?.sections.length ?? 0) === 1;

  return buildVisualItems(rows, rowsWithActive, activeWasAlone, srcRowIdxInAll);
}

/** Insert the drop placeholder at `insertIdx` when this row is the hover target. */
export function buildRowItems(
  row: VisualRow,
  rowIdx: number,
  dropPosition: DashboardDropPosition | null,
): RowItem[] {
  const rowItems: RowItem[] = row.sections.map((s) => ({
    kind: 'section' as const,
    section: s,
  }));

  if (dropPosition?.targetRowIdx === rowIdx) {
    const w: ElementWidth =
      row.sections.length + 1 >= DASHBOARD_MAX_SECTIONS_PER_ROW ? '1/3' : '1/2';
    rowItems.splice(dropPosition.insertIdx, 0, {
      kind: 'drop-placeholder' as const,
      width: w,
    });
  }

  return rowItems;
}

/** Insert `activeSection` into a wrap row at the drop index, or keep `current`. */
function insertSectionIntoRow(
  current: DashboardSection[],
  rowsWithoutActive: VisualRow[],
  activeSection: DashboardSection,
  dropPosition: DashboardDropPosition | null,
): DashboardSection[] {
  if (!dropPosition) return current;
  const targetRow = rowsWithoutActive[dropPosition.targetRowIdx];
  if (!targetRow) return current;

  const newTargetRow = [...targetRow.sections];
  newTargetRow.splice(dropPosition.insertIdx, 0, activeSection);

  const intendedRows: DashboardSection[][] = rowsWithoutActive.map((row, idx) =>
    idx === dropPosition.targetRowIdx ? newTargetRow : row.sections,
  );
  return buildSectionsFromRows(intendedRows);
}

/** Build wrap rows with `activeSection` as a new full-width row after `afterRowIdx`. */
function insertSectionAfterRow(
  rowsWithoutActive: VisualRow[],
  activeSection: DashboardSection,
  afterRowIdx: number,
): DashboardSection[] {
  const intendedRows: DashboardSection[][] = [];
  if (afterRowIdx === -1) {
    intendedRows.push([activeSection]);
  }
  rowsWithoutActive.forEach((row, idx) => {
    intendedRows.push(row.sections);
    if (idx === afterRowIdx) intendedRows.push([activeSection]);
  });
  return buildSectionsFromRows(intendedRows);
}

/** Commit insert into a wrap row or a new row after a between-row drop zone. */
export function applySectionDragEnd(
  currentSections: DashboardSection[],
  activeIdStr: string,
  overId: string,
  dropPosition: DashboardDropPosition | null,
): DashboardSection[] {
  const activeSection = currentSections.find((s) => s.id === activeIdStr);
  if (!activeSection) return currentSections;

  const sectionsWithoutActive = currentSections.filter((s) => s.id !== activeIdStr);
  const rowsWithoutActive = calculateRows(sectionsWithoutActive);

  if (isRowDroppableId(overId)) {
    return insertSectionIntoRow(currentSections, rowsWithoutActive, activeSection, dropPosition);
  }

  if (isDropZoneAfterRowId(overId)) {
    return insertSectionAfterRow(
      rowsWithoutActive,
      activeSection,
      parseDropZoneAfterRowIdx(overId),
    );
  }

  return insertSectionIntoRow(currentSections, rowsWithoutActive, activeSection, dropPosition);
}

/** Resize one section and keep its wrap-row neighbor complementary (2-up only). */
export function applySectionResize(
  current: DashboardSection[],
  sectionId: string,
  width: ElementWidth,
): DashboardSection[] {
  const rows = calculateRows(current);
  const targetRow = rows.find((row) => row.sections.some((s) => s.id === sectionId));
  if (!targetRow) return current;

  if (targetRow.sections.length === 1) {
    return current.map((s) => (s.id === sectionId ? { ...s, width: 'full' } : s));
  }

  if (targetRow.sections.length >= DASHBOARD_MAX_SECTIONS_PER_ROW) {
    return current.map((s) =>
      targetRow.sections.some((rs) => rs.id === s.id) ? { ...s, width: '1/3' } : s,
    );
  }

  const otherSection = targetRow.sections.find((s) => s.id !== sectionId);
  if (!otherSection) return current;

  return current.map((s) => {
    if (s.id === sectionId) return { ...s, width };
    if (s.id === otherSection.id) return { ...s, width: DASHBOARD_COMPLEMENTARY_WIDTH[width] };
    return s;
  });
}
